import {announce} from '@github-ui/aria-live'
import {useCreateScribeArtifact} from '@github-ui/scribe-artifacts'
import {Banner, FormControl, Textarea, TextInput} from '@primer/react'
import {Dialog, type DialogHeaderProps} from '@primer/react/experimental'
import {type ChangeEvent, type FormEvent, type RefObject, useCallback, useId, useRef, useState} from 'react'

import styles from './ArtifactsSection.module.css'

const CREATE_ARTIFACT_DIALOG_TITLE = 'Create new artifact'
const CREATE_ARTIFACT_BODY_PLACEHOLDER = 'Write markdown...'
const MAX_DERIVED_ARTIFACT_NAME_LENGTH = 100

export function deriveArtifactName(body: string): string {
  return (
    body
      .trim()
      .split(/\r?\n/)
      .find(line => line.trim())
      ?.trim()
      .slice(0, MAX_DERIVED_ARTIFACT_NAME_LENGTH) ?? ''
  )
}

type CreateArtifactDialogProps = {
  resourceId: number
  onClose: () => void
  onCreated: () => void
  returnFocusRef: RefObject<HTMLElement | null>
}

export function CreateArtifactDialog({resourceId, onClose, onCreated, returnFocusRef}: CreateArtifactDialogProps) {
  const formId = useId()
  const titleLabelId = `${formId}-title-label`
  const markdownInputRef = useRef<HTMLTextAreaElement>(null)
  const [body, setBody] = useState('')
  const [customTitle, setCustomTitle] = useState('')
  const [titleEdited, setTitleEdited] = useState(false)
  const [createErrorMessage, setCreateErrorMessage] = useState('')
  const {mutate: createArtifact, isPending} = useCreateScribeArtifact({resourceType: 'issue', resourceId})

  const derivedTitle = deriveArtifactName(body)
  const artifactName = (titleEdited ? customTitle : derivedTitle).trim()
  const displayedTitle = titleEdited ? customTitle : derivedTitle
  const bodyIsEmpty = body.trim().length === 0

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      createArtifact(
        {
          name: artifactName || CREATE_ARTIFACT_DIALOG_TITLE,
          body,
          type: 'generic',
        },
        {
          onSuccess: () => {
            onCreated()
            onClose()
          },
          onError: () => {
            const message = 'Artifact could not be created. Try again.'
            setCreateErrorMessage(message)
            announce(message, {assertive: true})
          },
        },
      )
    },
    [artifactName, body, createArtifact, onClose, onCreated],
  )

  const handleTitleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setTitleEdited(true)
    setCustomTitle(event.currentTarget.value)
  }, [])
  const handleBodyChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setBody(event.currentTarget.value)
    setCreateErrorMessage('')
  }, [])

  const renderHeader = useCallback(
    ({onClose: closeDialog, dialogDescriptionId, dialogLabelId}: DialogHeaderProps) => (
      <Dialog.Header className={styles.CreateArtifactHeader}>
        <Dialog.Title id={dialogLabelId} className="sr-only">
          {CREATE_ARTIFACT_DIALOG_TITLE}
        </Dialog.Title>
        <span id={dialogDescriptionId} className="sr-only">
          Write markdown to create an artifact.
        </span>
        <div className={styles.CreateArtifactTitle}>
          <span id={titleLabelId} className="sr-only">
            Artifact title
          </span>
          <TextInput
            value={displayedTitle}
            aria-labelledby={titleLabelId}
            placeholder={CREATE_ARTIFACT_DIALOG_TITLE}
            block
            className={styles.CreateArtifactTitleInput}
            disabled={isPending}
            onChange={handleTitleChange}
          />
        </div>
        <div className={styles.CreateArtifactHeaderActions}>
          <Dialog.CloseButton onClose={() => closeDialog('close-button')} />
        </div>
      </Dialog.Header>
    ),
    [displayedTitle, handleTitleChange, isPending, titleLabelId],
  )

  return (
    <Dialog
      title={CREATE_ARTIFACT_DIALOG_TITLE}
      width="large"
      onClose={onClose}
      initialFocusRef={markdownInputRef}
      returnFocusRef={returnFocusRef}
      renderHeader={renderHeader}
      footerButtons={[
        {content: 'Cancel', onClick: onClose, disabled: isPending},
        {
          content: 'Create artifact',
          buttonType: 'primary',
          type: 'submit',
          form: formId,
          disabled: bodyIsEmpty || isPending,
          loading: isPending,
          loadingAnnouncement: 'Creating artifact',
        },
      ]}
    >
      <form id={formId} onSubmit={handleSubmit} className={styles.CreateArtifactForm}>
        {createErrorMessage && (
          <Banner title="Create artifact failed" variant="critical" description={createErrorMessage} />
        )}
        <FormControl required disabled={isPending}>
          <FormControl.Label>Markdown</FormControl.Label>
          <Textarea
            ref={markdownInputRef}
            value={body}
            placeholder={CREATE_ARTIFACT_BODY_PLACEHOLDER}
            rows={16}
            className={styles.CreateArtifactTextarea}
            onChange={handleBodyChange}
          />
        </FormControl>
      </form>
    </Dialog>
  )
}
